import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import apiClient from '../../services/apiClient';
import LocationPicker from '../../components/logistica/LocationPicker.jsx';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import ConfiguracionObras from './ConfiguracionObras';
import {
  Page, Header, Title, Controls, SearchInput, BackBtn, AddBtn,
  TableWrap, Table, Th, Td, ActionsCell, ActionBtn,
  Overlay, ModalBox, ModalHead, ModalTitle, CloseBtn, ModalBody, ModalFoot,
  FormGrp, Lbl, Inp, Row2, ErrMsg, BtnPrimary, BtnSecondary, BtnDanger,
} from '../../components/configuracion/logisticaLayoutStyles';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';

const FieldWithAddRow = styled.div`
  display: flex; align-items: flex-end; gap: 0.4rem;
  > *:first-child { flex: 1; min-width: 0; }
`;

const SmallAddBtn = styled.button`
  flex: 0 0 34px; width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.success};
  background: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.success};
  border-radius: 6px; cursor: pointer; font-size: 0.85rem;
  transition: background .15s;
  &:hover { background: ${({ theme }) => theme.colors.success}15; }
  i { pointer-events: none; }
`;

const SectionLabel = styled.div`
  font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.txtSecondary}; margin: 1rem 0 0.5rem;
  padding-bottom: 0.25rem; border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

const StyledSelect = styled.select`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem; min-height: 34px; cursor: pointer;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  option { background: ${({ theme }) => theme.colors.surface}; }
`;

const BulkEditBtn = styled(AddBtn)`
  background: ${({ theme, $active }) => $active ? theme.colors.warning : theme.colors.transparent};
  color: ${({ theme, $active }) => $active ? theme.colors.onPrimary : theme.colors.warning};
  border: 1px solid ${({ theme }) => theme.colors.warning};

  &:hover {
    background: ${({ theme }) => theme.colors.warning};
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

const ContinuarBtn = styled(AddBtn)`
  background: ${({ theme }) => theme.colors.success};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: 1px solid ${({ theme }) => theme.colors.success};

  &:hover {
    background: ${({ theme }) => theme.colors.success};
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const BulkCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.info};
`;

const EMPTY = {
  nombre: '', provincia: '', localidad: '', direccion: '', codigo_postal: '',
  latitud: '', longitud: '',
  contacto_nombre: '', contacto_telefono: '', contacto_correo: '', contacto_cuit: '',
  obra: '',
  distancia_no_calculable_km: '',
};

export default function ConfiguracionUbicacionesLogisticas({ modalOnly = false, onRecordSaved = null, onRequestClose = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backTo = searchParams.get('from') === 'org'
    ? '/configuracion?seccion=org'
    : '/configuracion?seccion=logistica';
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();
  const [items, setItems] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkObraId, setBulkObraId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showAgregarObra, setShowAgregarObra] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [ubResp, obraResp] = await Promise.all([
        apiClient.get('ubicaciones/'),
        apiClient.get('obras/?page_size=10000'),
      ]);
      setItems(Array.isArray(ubResp.data) ? ubResp.data : (ubResp.data.results || []));
      setObras(Array.isArray(obraResp.data) ? obraResp.data : (obraResp.data.results || []));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const handleObraCreada = (savedRecord) => {
    if (savedRecord?.id != null) {
      setObras(prev => [...prev.filter(o => o.id !== savedRecord.id), savedRecord]);
      set('obra', String(savedRecord.id));
    }
    setShowAgregarObra(false);
  };

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => { if (modalOnly) { abrir(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return items;
    const q = busqueda.toLowerCase();
    return items.filter(i =>
      i.nombre?.toLowerCase().includes(q) ||
      i.localidad?.toLowerCase().includes(q) ||
      i.provincia?.toLowerCase().includes(q) ||
      i.obra_nombre?.toLowerCase().includes(q)
    );
  }, [items, busqueda]);

  const [mapLocation, setMapLocation] = useState(null);

  const abrir = (item = null) => {
    setEditando(item);
    const lat = item?.latitud != null ? String(item.latitud) : '';
    const lng = item?.longitud != null ? String(item.longitud) : '';
    setForm(item ? {
      nombre: item.nombre || '', provincia: item.provincia || '',
      localidad: item.localidad || '', direccion: item.direccion || '',
      codigo_postal: item.codigo_postal || '',
      latitud: lat, longitud: lng,
      contacto_nombre: item.contacto_nombre || '', contacto_telefono: item.contacto_telefono || '',
      contacto_correo: item.contacto_correo || '', contacto_cuit: item.contacto_cuit || '',
      obra: item.obra != null ? String(item.obra) : '',
      distancia_no_calculable_km: item.distancia_no_calculable_km != null ? String(item.distancia_no_calculable_km) : '',
    } : EMPTY);
    setMapLocation(lat && lng ? {
      address: item?.direccion || '', lat: parseFloat(lat), lng: parseFloat(lng),
      direccion: item?.direccion || '', localidad: item?.localidad || '', provincia: item?.provincia || '',
    } : null);
    setErrors({});
    setModal(true);
  };

  const handleMapLocation = (data) => {
    setMapLocation(data);
    if (!data) {
      setForm(f => ({ ...f, latitud: '', longitud: '' }));
      return;
    }
    const lat = data.lat != null && !isNaN(data.lat) ? String(data.lat) : '';
    const lng = data.lng != null && !isNaN(data.lng) ? String(data.lng) : '';
    setForm(f => ({
      ...f,
      latitud: lat,
      longitud: lng,
      direccion: data.direccion || f.direccion,
      localidad: data.localidad || f.localidad,
      provincia: data.provincia || f.provincia,
    }));
  };

  const cerrar = () => { setModal(false); setEditando(null); onRequestClose?.(); };
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  const guardar = async () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        provincia: form.provincia, localidad: form.localidad,
        direccion: form.direccion, codigo_postal: form.codigo_postal,
        latitud: (() => { const v = parseFloat(form.latitud); return isNaN(v) ? null : parseFloat(v.toFixed(8)); })(),
        longitud: (() => { const v = parseFloat(form.longitud); return isNaN(v) ? null : parseFloat(v.toFixed(8)); })(),
        contacto_nombre: form.contacto_nombre, contacto_telefono: form.contacto_telefono,
        contacto_correo: form.contacto_correo, contacto_cuit: form.contacto_cuit,
        obra: form.obra !== '' ? parseInt(form.obra) : null,
        distancia_no_calculable_km: form.distancia_no_calculable_km !== '' ? parseFloat(form.distancia_no_calculable_km) : null,
      };
      let savedRecord;
      if (editando) {
        const resp = await apiClient.patch(`ubicaciones/${editando.id}/`, payload);
        savedRecord = resp.data;
      } else {
        const resp = await apiClient.post('ubicaciones/', payload);
        savedRecord = resp.data;
      }
      if (modalOnly && onRecordSaved) {
        onRecordSaved(savedRecord);
        onRequestClose?.();
      } else {
        cerrar(); cargar();
      }
    } catch (err) {
      console.error('Error guardando ubicación:', err.response?.status, err.response?.data);
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        setErrors(detail);
      } else {
        setErrors({ nombre: 'No se pudo guardar. Verificá los datos e intentá nuevamente.' });
      }
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    try { await apiClient.delete(`ubicaciones/${id}/`); setConfirmId(null); cargar(); }
    catch (e) { console.error(e); }
  };

  const toggleBulkMode = () => {
    setBulkMode(v => !v);
    setSelectedIds(new Set());
  };

  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => (prev.size === filtrados.length ? new Set() : new Set(filtrados.map(i => i.id))));
  };

  const abrirBulkModal = () => {
    setBulkObraId('');
    setShowBulkModal(true);
  };

  const guardarMasivo = async () => {
    setBulkSaving(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(
        ids.map(id => apiClient.patch(`ubicaciones/${id}/`, { obra: bulkObraId !== '' ? parseInt(bulkObraId) : null }))
      );
      await cargar();
      setBulkMode(false);
      setSelectedIds(new Set());
      setShowBulkModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      setBulkSaving(false);
    }
  };

  if (modalOnly) {
    return (
      <>
      {modal && (
      <Overlay onMouseDown={onBackdropMouseDown} onClick={buildOnBackdropClick(cerrar)}>
        <ModalBox $maxWidth="600px" onClick={e => e.stopPropagation()}>
          <ModalHead>
            <ModalTitle>Nueva ubicación</ModalTitle>
            <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
          </ModalHead>
          <ModalBody>
            <FormGrp>
              <Lbl>Nombre *</Lbl>
              <Inp autoFocus value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre de la ubicación" />
              {errors.nombre && <ErrMsg>{errors.nombre}</ErrMsg>}
            </FormGrp>
            <FormGrp>
              <Lbl>Obra vinculada</Lbl>
              <FieldWithAddRow>
                <StyledSelect value={form.obra} onChange={e => set('obra', e.target.value)}>
                  <option value="">— Sin obra (ubicación independiente) —</option>
                  {obras.map(o => (
                    <option key={o.id} value={String(o.id)}>{o.nombre}</option>
                  ))}
                </StyledSelect>
                <SmallAddBtn type="button" tabIndex={-1} title="Agregar obra" onClick={() => setShowAgregarObra(true)}>
                  <i className="fas fa-plus" />
                </SmallAddBtn>
              </FieldWithAddRow>
              <small style={{ display: 'block', marginTop: 4, fontSize: '0.72rem', opacity: 0.6 }}>
                Al finalizar un movimiento logístico con destino aquí, los equipos pasarán a esta obra.
              </small>
            </FormGrp>
            <FormGrp>
              <Lbl>Ubicación en mapa</Lbl>
              <LocationPicker key={editando?.id ?? 'nuevo'} value={mapLocation} onChange={handleMapLocation} placeholder="Buscar en el mapa..." />
              {(form.latitud || form.longitud) && (
                <small style={{ display: 'block', marginTop: 4, fontSize: '0.72rem', opacity: 0.6 }}>
                  📍 {form.latitud}, {form.longitud}
                </small>
              )}
            </FormGrp>
            <Row2>
              <FormGrp>
                <Lbl>Provincia</Lbl>
                <Inp value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" />
              </FormGrp>
              <FormGrp>
                <Lbl>Localidad</Lbl>
                <Inp value={form.localidad} onChange={e => set('localidad', e.target.value)} placeholder="Localidad" />
              </FormGrp>
            </Row2>
            <Row2>
              <FormGrp>
                <Lbl>Dirección</Lbl>
                <Inp value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle y número" />
              </FormGrp>
              <FormGrp>
                <Lbl>Código postal</Lbl>
                <Inp value={form.codigo_postal} onChange={e => set('codigo_postal', e.target.value)} placeholder="CP" />
              </FormGrp>
            </Row2>
            <Row2>
              <FormGrp>
                <Lbl>Contacto</Lbl>
                <Inp value={form.contacto_nombre} onChange={e => set('contacto_nombre', e.target.value)} placeholder="Nombre del contacto" />
              </FormGrp>
              <FormGrp>
                <Lbl>Teléfono</Lbl>
                <Inp value={form.contacto_telefono} onChange={e => set('contacto_telefono', e.target.value)} placeholder="Teléfono" />
              </FormGrp>
            </Row2>
            <FormGrp>
              <Lbl>Distancia no calculable (km)</Lbl>
              <Inp
                type="number"
                min="0"
                step="0.1"
                value={form.distancia_no_calculable_km}
                onChange={e => set('distancia_no_calculable_km', e.target.value)}
                placeholder="ej. 5.5 — acceso a obra, pista minera..."
              />
              <small style={{ display: 'block', marginTop: 4, fontSize: '0.72rem', opacity: 0.6 }}>
                Se suma automáticamente a la ruta calculada en cada movimiento que use esta ubicación.
              </small>
            </FormGrp>
          </ModalBody>
          <ModalFoot>
            <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
            <BtnPrimary onClick={guardar} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</BtnPrimary>
          </ModalFoot>
        </ModalBox>
      </Overlay>
      )}
      {showAgregarObra && (
        <ConfiguracionObras
          modalOnly
          modalZIndex={9100}
          onRecordSaved={handleObraCreada}
          onRequestClose={() => setShowAgregarObra(false)}
        />
      )}
      </>
    );
  }

  return (
    <Page>
      <Header>
        <Title><i className="fas fa-map-marker-alt" /> Ubicaciones Logísticas</Title>
        <HelpToggleBtn
          type="button"
          $active={helpOpen}
          onClick={() => setHelpOpen(v => !v)}
        >
          <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
          Ayuda
        </HelpToggleBtn>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            tips={[
              'Son los puntos de origen y destino que se eligen al programar un traslado: obras, depósitos, talleres, centrales, etc.',
              'Cargar la ubicación con su dirección real permite después calcular distancias y costos de viaje.',
              'Usá nombres que el personal de logística reconozca, no códigos internos.',
              'Podes agregar en cada ubicación tramos de ruta no calculables. Son tramos donde Google Maps no puede calcular la distancia, como accesos a obras, pistas mineras, etc. Esa distancia se suma automáticamente a la ruta calculada en cada movimiento que use esta ubicación.',
            ]}
            nextSteps={[
              'Seleccionar origen y destino al registrar cada movimiento logístico.',
              'Asignar la ubicación a las obras.',
              'Consultar el informe de rutas para ver los trayectos más frecuentes.',
            ]}
            inlineOpen
          />
        </div>
      )}
      <Controls>
        <SearchInput $width="280px" placeholder="Buscar ubicación..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <BackBtn onClick={() => navigate(backTo)}>
            <i className="fas fa-arrow-left" /> Volver
          </BackBtn>
          {bulkMode ? (
            <>
              <BulkEditBtn onClick={toggleBulkMode}>
                <i className="fas fa-times" /> Cancelar
              </BulkEditBtn>
              {selectedIds.size >= 2 && (
                <ContinuarBtn onClick={abrirBulkModal}>
                  <i className="fas fa-check" /> Continuar ({selectedIds.size})
                </ContinuarBtn>
              )}
            </>
          ) : (
            <>
              <BulkEditBtn onClick={toggleBulkMode} title="Editar campos en común de varias ubicaciones a la vez">
                <i className="fas fa-layer-group" /> Editar campos
              </BulkEditBtn>
              <AddBtn onClick={() => abrir()}><i className="fas fa-plus" /> Agregar</AddBtn>
            </>
          )}
        </div>
      </Controls>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              {bulkMode && (
                <Th style={{ width: 36, textAlign: 'center' }}>
                  <BulkCheckbox
                    checked={filtrados.length > 0 && selectedIds.size === filtrados.length}
                    onChange={toggleSelectAll}
                    title="Seleccionar todos"
                  />
                </Th>
              )}
              <Th>Nombre</Th>
              <Th>Provincia</Th>
              <Th>Localidad</Th>
              <Th>Obra vinculada</Th>
              <Th>Contacto</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td colSpan={bulkMode ? 7 : 6} style={{ textAlign: 'center', padding: '2rem' }}>Cargando...</Td></tr>}
            {!loading && filtrados.length === 0 && <tr><Td colSpan={bulkMode ? 7 : 6} style={{ textAlign: 'center', padding: '2rem' }}>Sin registros</Td></tr>}
            {filtrados.map(item => (
              <tr
                key={item.id}
                onClick={bulkMode ? () => toggleSelectId(item.id) : undefined}
                style={bulkMode ? { cursor: 'pointer', userSelect: 'none' } : undefined}
              >
                {bulkMode && (
                  <Td style={{ textAlign: 'center' }}>
                    {/* El toggle real lo maneja el onClick del <tr>; este checkbox es solo visual. */}
                    <BulkCheckbox checked={selectedIds.has(item.id)} onChange={() => {}} />
                  </Td>
                )}
                <Td>{item.nombre}</Td>
                <Td>{item.provincia || '-'}</Td>
                <Td>{item.localidad || '-'}</Td>
                <Td>{item.obra_nombre || <span style={{ opacity: 0.4 }}>—</span>}</Td>
                <Td>{item.contacto_nombre || '-'}</Td>
                <ActionsCell onClick={(e) => bulkMode && e.stopPropagation()}>
                  <ActionBtn onClick={() => abrir(item)}>Editar</ActionBtn>
                  <ActionBtn $danger onClick={() => setConfirmId(item.id)}>Eliminar</ActionBtn>
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {modal && (
        <Overlay onMouseDown={onBackdropMouseDown} onClick={buildOnBackdropClick(cerrar)}>
          <ModalBox $maxWidth="600px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{editando ? 'Editar ubicación' : 'Nueva ubicación'}</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
            </ModalHead>
            <ModalBody>
              <FormGrp>
                <Lbl>Nombre *</Lbl>
                <Inp autoFocus value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre de la ubicación" />
                {errors.nombre && <ErrMsg>{errors.nombre}</ErrMsg>}
              </FormGrp>
              <FormGrp>
                <Lbl>Obra vinculada</Lbl>
                <FieldWithAddRow>
                  <StyledSelect value={form.obra} onChange={e => set('obra', e.target.value)}>
                    <option value="">— Sin obra (ubicación independiente) —</option>
                    {obras.map(o => (
                      <option key={o.id} value={String(o.id)}>{o.nombre}</option>
                    ))}
                  </StyledSelect>
                  <SmallAddBtn type="button" tabIndex={-1} title="Agregar obra" onClick={() => setShowAgregarObra(true)}>
                    <i className="fas fa-plus" />
                  </SmallAddBtn>
                </FieldWithAddRow>
                <small style={{ display: 'block', marginTop: 4, fontSize: '0.72rem', opacity: 0.6 }}>
                  Al finalizar un movimiento logístico con destino aquí, los equipos pasarán a esta obra.
                </small>
              </FormGrp>
              <FormGrp>
                <Lbl>Ubicación en mapa</Lbl>
                <LocationPicker
                  key={editando?.id ?? 'nuevo'}
                  value={mapLocation}
                  onChange={handleMapLocation}
                  placeholder="Buscar la ubicación en el mapa..."
                />
                {(form.latitud || form.longitud) && (
                  <small style={{ display: 'block', marginTop: 4, fontSize: '0.72rem', opacity: 0.6 }}>
                    📍 {form.latitud}, {form.longitud}
                  </small>
                )}
              </FormGrp>
              <Row2>
                <FormGrp>
                  <Lbl>Provincia</Lbl>
                  <Inp value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" />
                </FormGrp>
                <FormGrp>
                  <Lbl>Localidad</Lbl>
                  <Inp value={form.localidad} onChange={e => set('localidad', e.target.value)} placeholder="Localidad" />
                </FormGrp>
              </Row2>
              <Row2>
                <FormGrp>
                  <Lbl>Dirección</Lbl>
                  <Inp value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle y número" />
                </FormGrp>
                <FormGrp>
                  <Lbl>Código postal</Lbl>
                  <Inp value={form.codigo_postal} onChange={e => set('codigo_postal', e.target.value)} placeholder="CP" />
                </FormGrp>
              </Row2>
              <FormGrp>
                <Lbl>Distancia no calculable (km)</Lbl>
                <Inp
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.distancia_no_calculable_km}
                  onChange={e => set('distancia_no_calculable_km', e.target.value)}
                  placeholder="ej. 5.5 — acceso a obra, pista minera..."
                />
                <small style={{ display: 'block', marginTop: 4, fontSize: '0.72rem', opacity: 0.6 }}>
                  Se suma automáticamente a la ruta calculada en cada movimiento que use esta ubicación.
                </small>
              </FormGrp>
              <SectionLabel>Datos de contacto</SectionLabel>
              <Row2>
                <FormGrp>
                  <Lbl>Nombre del contacto</Lbl>
                  <Inp value={form.contacto_nombre} onChange={e => set('contacto_nombre', e.target.value)} placeholder="Nombre" />
                </FormGrp>
                <FormGrp>
                  <Lbl>Teléfono</Lbl>
                  <Inp value={form.contacto_telefono} onChange={e => set('contacto_telefono', e.target.value)} placeholder="Teléfono" />
                </FormGrp>
              </Row2>
              <Row2>
                <FormGrp>
                  <Lbl>Correo electrónico</Lbl>
                  <Inp type="email" value={form.contacto_correo} onChange={e => set('contacto_correo', e.target.value)} placeholder="correo@ejemplo.com" />
                </FormGrp>
                <FormGrp>
                  <Lbl>CUIT</Lbl>
                  <Inp value={form.contacto_cuit} onChange={e => set('contacto_cuit', e.target.value)} placeholder="XX-XXXXXXXX-X" />
                </FormGrp>
              </Row2>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
              <BtnPrimary onClick={guardar} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</BtnPrimary>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {showAgregarObra && (
        <ConfiguracionObras
          modalOnly
          modalZIndex={9100}
          onRecordSaved={handleObraCreada}
          onRequestClose={() => setShowAgregarObra(false)}
        />
      )}

      {confirmId && (
        <Overlay onMouseDown={onBackdropMouseDown} onClick={buildOnBackdropClick(() => setConfirmId(null))}>
          <ModalBox $maxWidth="380px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Confirmar eliminación</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <ModalBody><p>¿Eliminar esta ubicación? Esta acción no se puede deshacer.</p></ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirmId(null)}>Cancelar</BtnSecondary>
              <BtnDanger onClick={() => eliminar(confirmId)}>Eliminar</BtnDanger>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {showBulkModal && (
        <Overlay onMouseDown={onBackdropMouseDown} onClick={buildOnBackdropClick(() => setShowBulkModal(false))}>
          <ModalBox $maxWidth="420px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Editar campos en común</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setShowBulkModal(false)}>×</CloseBtn>
            </ModalHead>
            <ModalBody>
              <p style={{ fontSize: '0.85rem', opacity: 0.75, marginTop: 0 }}>
                {selectedIds.size} ubicaciones seleccionadas. El nombre de cada una se mantiene intacto.
              </p>
              <FormGrp>
                <Lbl>Obra vinculada</Lbl>
                <StyledSelect value={bulkObraId} onChange={e => setBulkObraId(e.target.value)}>
                  <option value="">— Sin obra (ubicación independiente) —</option>
                  {obras.map(o => (
                    <option key={o.id} value={String(o.id)}>{o.nombre}</option>
                  ))}
                </StyledSelect>
              </FormGrp>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setShowBulkModal(false)} disabled={bulkSaving}>Cancelar</BtnSecondary>
              <BtnPrimary onClick={guardarMasivo} disabled={bulkSaving}>
                {bulkSaving ? 'Guardando...' : `Guardar en ${selectedIds.size} elemento${selectedIds.size !== 1 ? 's' : ''}`}
              </BtnPrimary>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}
    </Page>
  );
}
